import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { HiPhone, HiMail, HiLocationMarker, HiArrowUp } from "react-icons/hi";
import { useRef } from "react";

const services = [
  { name: "Public & Private Drainage", path: "/services/drainage" },
  { name: "Drainlaying", path: "/services/drainlaying" },
  { name: "Drain Unblocking", path: "/services/drain-unblocking" },
  { name: "Rainwater Tank Installation", path: "/services/rainwater-tank-install" },
  { name: "Wastewater Systems", path: "/services/wastewater-systems" },
  { name: "Earthworks & Demolition", path: "/services/earthworks-demolition" },
  { name: "Concrete & Civil Work", path: "/services/concrete-civil" },
  { name: "Commercial Plumbing", path: "/services/plumbing" },
];

const quickLinks = [
  { name: "Home", path: "/" },
  { name: "About Us", path: "/about" },
  { name: "Services", path: "/services" },
  { name: "Testimonials", path: "/testimonials" },
  { name: "Contact", path: "/contact" },
  { name: "Privacy Policy", path: "/privacy" },
  { name: "Terms of Service", path: "/terms" },
];

const areas = [
  { name: "Hillsborough (HQ)", path: "/service-areas/central-isthmus/hillsborough" },
  { name: "Onehunga", path: "/service-areas/central-isthmus/onehunga" },
  { name: "Mount Roskill", path: "/service-areas/central-isthmus/mt-roskill" },
  { name: "Papakura & Drury", path: "/service-areas/south-auckland/papakura-drury" },
  { name: "West Auckland", path: "/service-areas/west-auckland/new-lynn-henderson" },
  { name: "Central Isthmus", path: "/service-areas/central-isthmus/hillsborough" },
  { name: "South Auckland", path: "/service-areas/south-auckland/papakura-drury" },
  { name: "New Lynn & Henderson", path: "/service-areas/west-auckland/new-lynn-henderson" },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as const } },
};

export default function Footer() {
  const footerRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: footerRef,
    offset: ["start end", "end end"],
  });

  const bgY = useTransform(scrollYProgress, [0, 1], [100, 0]);
  const orb1X = useTransform(scrollYProgress, [0, 1], [-50, 50]);
  const orb2X = useTransform(scrollYProgress, [0, 1], [50, -30]);
  const orb1Scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.6, 1.2, 1]);
  const orb2Scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.8, 1.3, 0.9]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.3], [0, 1]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer
      ref={footerRef}
      className="relative text-white overflow-hidden"
    >
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900 via-[#0a0a0a] to-black" />

        <motion.div
          style={{ y: bgY }}
          className="absolute inset-0 opacity-50"
        >
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(255,255,255,0.08)_0%,transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(200,200,200,0.06)_0%,transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(180,180,180,0.04)_0%,transparent_40%)]" />
        </motion.div>

        <motion.div
          style={{ x: orb1X, scale: orb1Scale }}
          className="absolute top-20 left-1/4 w-[500px] h-[500px] rounded-full pointer-events-none"
        >
          <div className="w-full h-full bg-gradient-to-br from-white/10 via-gray-400/5 to-transparent blur-[150px] rounded-full" />
        </motion.div>
        <motion.div
          style={{ x: orb2X, scale: orb2Scale }}
          className="absolute bottom-20 right-1/4 w-[400px] h-[400px] rounded-full pointer-events-none"
        >
          <div className="w-full h-full bg-gradient-to-tl from-gray-400/10 via-white/4 to-transparent blur-[130px] rounded-full" />
        </motion.div>

        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />
      </div>

      <div className="absolute top-0 left-0 w-full overflow-hidden leading-[0]">
        <svg
          className="relative block w-[calc(100%+1.3px)] h-[180px] transform rotate-180"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="waveGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#9ca3af" stopOpacity="0.05" />
              <stop offset="50%" stopColor="#d1d5db" stopOpacity="0.12" />
              <stop offset="100%" stopColor="#9ca3af" stopOpacity="0.05" />
            </linearGradient>
          </defs>
          <path
            d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,122.7C672,117,768,139,864,154.7C960,171,1056,181,1152,165.3C1248,149,1344,107,1392,85.3L1440,64L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"
            fill="url(#waveGrad)"
          />
        </svg>
      </div>

      <motion.div
        style={{ opacity: contentOpacity }}
        className="max-w-7xl mx-auto px-6 relative z-10 pt-48"
      >
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="grid grid-cols-1 lg:grid-cols-4 gap-12 mb-24"
        >
          <motion.div variants={itemVariants} className="space-y-8">
            <Link to="/" className="inline-block">
              <motion.img
                whileHover={{ scale: 1.05, filter: "brightness(1.2)" }}
                transition={{ duration: 0.3 }}
                src="/media/asset-01.png"
                alt="Craft Civil Contractors"
                width={180}
                height={45}
                className="h-18 w-auto"
              />
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed">
              Professional civil contracting services across Auckland. Quality
              workmanship and reliable service guaranteed for over 15 years.
            </p>
            <div className="space-y-3">
              <motion.a
                href="tel:+642102984443"
                whileHover={{ x: 5, backgroundColor: "rgba(255,255,255,0.1)" }}
                className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-white/5 to-transparent border border-white/10 hover:border-white/30 transition-all group"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-white/20 to-gray-500/20 flex items-center justify-center group-hover:from-white group-hover:to-gray-200 transition-all shadow-inner">
                  <HiPhone className="w-5 h-5 text-gray-300 group-hover:text-black transition-colors" />
                </div>
                <span className="text-sm font-medium text-gray-300 group-hover:text-white transition-colors">
                  +64 021 029 84443
                </span>
              </motion.a>

              <motion.a
                href="mailto:admin@craftcivil.co.nz?subject=Website%20Enquiry%20-%20Craft%20Civil"
                whileHover={{ x: 5, backgroundColor: "rgba(255,255,255,0.1)" }}
                className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-white/5 to-transparent border border-white/10 hover:border-white/30 transition-all group"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-white/20 to-gray-500/20 flex items-center justify-center group-hover:from-white group-hover:to-gray-200 transition-all shadow-inner">
                  <HiMail className="w-5 h-5 text-gray-300 group-hover:text-black transition-colors" />
                </div>
                <span className="text-sm font-medium text-gray-300 group-hover:text-white transition-colors">
                  admin@craftcivil.co.nz
                </span>
              </motion.a>

              <motion.div
                whileHover={{ x: 5 }}
                className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-white/5 to-transparent border border-white/10 group"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-white/20 to-gray-500/20 flex items-center justify-center shadow-inner">
                  <HiLocationMarker className="w-5 h-5 text-gray-300" />
                </div>
                <span className="text-sm font-medium text-gray-300">
                  110 Carlton Street, Hillsborough
                </span>
              </motion.div>
            </div>
          </motion.div>

          <motion.div variants={itemVariants}>
            <h3 className="text-xl font-bold mb-8 font-heading relative inline-block">
              Our Services
              <motion.div
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className="absolute -bottom-2 left-0 h-0.5 w-full bg-gradient-to-r from-white via-gray-400 to-transparent origin-left"
              />
            </h3>
            <ul className="space-y-4">
              {services.map((service, i) => (
                <motion.li
                  key={service.path}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 + i * 0.08 }}
                >
                  <Link
                    to={service.path}
                    className="text-gray-400 hover:text-white transition-all flex items-center gap-3 group py-1"
                  >
                    <motion.span
                      whileHover={{ scale: 1.5 }}
                      className="w-2 h-2 rounded-full bg-gradient-to-br from-gray-400/60 to-gray-500/40 group-hover:from-white group-hover:to-gray-300 transition-all shadow-[0_0_8px_rgba(255,255,255,0.2)] group-hover:shadow-[0_0_12px_rgba(255,255,255,0.4)]"
                    />
                    <span className="group-hover:translate-x-1 transition-transform duration-300">
                      {service.name}
                    </span>
                  </Link>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          <motion.div variants={itemVariants}>
            <h3 className="text-xl font-bold mb-8 font-heading relative inline-block">
              Quick Links
              <motion.div
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6, duration: 0.6 }}
                className="absolute -bottom-2 left-0 h-0.5 w-full bg-gradient-to-r from-white via-gray-400 to-transparent origin-left"
              />
            </h3>
            <nav aria-label="Footer Navigation">
              <ul className="space-y-4">
                {quickLinks.map((link, i) => (
                  <motion.li
                    key={link.path}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + i * 0.08 }}
                  >
                    <Link
                      to={link.path}
                      className="text-gray-400 hover:text-white transition-all flex items-center gap-3 group py-1"
                    >
                      <motion.span
                        whileHover={{ scale: 1.5 }}
                        className="w-2 h-2 rounded-full bg-gradient-to-br from-gray-400/60 to-gray-500/40 group-hover:from-white group-hover:to-gray-300 transition-all shadow-[0_0_8px_rgba(255,255,255,0.2)] group-hover:shadow-[0_0_12px_rgba(255,255,255,0.4)]"
                      />
                      <span className="group-hover:translate-x-1 transition-transform duration-300">
                        {link.name}
                      </span>
                    </Link>
                  </motion.li>
                ))}
              </ul>
            </nav>
          </motion.div>

          <motion.div variants={itemVariants}>
            <h3 className="text-xl font-bold mb-8 font-heading relative inline-block">
              Service Areas
              <motion.div
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.7, duration: 0.6 }}
                className="absolute -bottom-2 left-0 h-0.5 w-full bg-gradient-to-r from-white via-gray-400 to-transparent origin-left"
              />
            </h3>
            <div className="flex flex-wrap gap-2">
              {areas.map((area, i) => (
                <Link key={area.name} to={area.path}>
                  <motion.span
                    initial={{ opacity: 0, scale: 0.5, y: 10 }}
                    whileInView={{ opacity: 1, scale: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      delay: 0.4 + i * 0.06,
                      type: "spring",
                      stiffness: 200,
                    }}
                    whileHover={{
                      scale: 1.08,
                      y: -3,
                      boxShadow: "0 10px 30px rgba(255,255,255,0.15)",
                    }}
                    className="inline-block px-4 py-2 rounded-full bg-gradient-to-br from-white/10 to-white/5 border border-white/15 text-xs text-gray-300 hover:bg-white hover:text-black hover:border-transparent transition-all cursor-pointer"
                  >
                    {area.name}
                  </motion.span>
                </Link>
              ))}
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
        >
          <div className="mb-10" />

          <div className="flex flex-col md:flex-row items-center justify-between gap-6 pb-12">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Craft Civil Contractors. All rights
              reserved.
            </p>

            <motion.button
              onClick={scrollToTop}
              aria-label="Scroll to top"
              whileHover={{ y: -5, scale: 1.1, boxShadow: "0 10px 30px rgba(255,255,255,0.2)" }}
              whileTap={{ scale: 0.95 }}
              className="w-14 h-14 rounded-full bg-gradient-to-br from-white/20 to-gray-500/10 border border-white/30 flex items-center justify-center hover:from-white hover:to-gray-200 hover:border-transparent text-gray-400 hover:text-black transition-all group shadow-lg"
            >
              <HiArrowUp className="w-6 h-6 transition-colors" />
            </motion.button>

            <motion.div
              whileHover={{ scale: 1.03, y: -2 }}
              className="relative px-12 py-6 rounded-2xl overflow-hidden group cursor-default"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-gray-500/5 to-white/10 backdrop-blur-xl" />
              <div className="absolute inset-0 border border-white/15 rounded-2xl group-hover:border-white/30 transition-colors" />
              <motion.div
                animate={{ x: ["-200%", "200%"] }}
                transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
                className="absolute inset-0 w-1/2 bg-gradient-to-r from-transparent via-white/20 to-transparent"
              />
              <div className="relative z-10 text-center">
                <span className="block text-xs text-gray-400 mb-1.5">
                  Designed & Developed by
                </span>
                <span className="levrix-gradient text-xl font-bold">Levrix Digital</span>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </footer>
  );
}
